/**
 * SaveToFinalPackageButton Component
 * Saves generated documents (PDFs) to the final package collection
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Save, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SaveToFinalPackageButton({
  clientId,
  bureau,
  docType,
  fileName,
  sourceStep,
  disabled = false,
  getFileData, // async function that returns Blob or base64 string
  onSuccess,
  className = ''
}) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!clientId || !getFileData) {
      toast.error('Missing required data for save');
      return;
    }

    setSaving(true);
    try {
      // Get the file data (either blob or base64)
      const fileData = await getFileData();
      
      let base64Data;
      if (fileData instanceof Blob) {
        // Convert blob to base64
        base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result;
            // Remove data URL prefix if present
            const base64 = result.includes(',') ? result.split(',')[1] : result;
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(fileData);
        });
      } else if (typeof fileData === 'string') {
        // Already base64
        base64Data = fileData.includes(',') ? fileData.split(',')[1] : fileData;
      } else {
        throw new Error('Invalid file data format');
      }

      // Save to final package
      const response = await axios.post(`${API}/api/final-package/save-artifact`, {
        client_id: clientId,
        bureau: bureau,
        doc_type: docType,
        file_name: fileName,
        source_step: sourceStep,
        file_data: base64Data
      });

      if (response.data?.success) {
        setSaved(true);
        toast.success(`${docType.replace(/_/g, ' ')} saved to final package`);
        if (onSuccess) {
          onSuccess(response.data);
        }
        // Reset saved state after a delay
        setTimeout(() => setSaved(false), 3000);
      } else {
        throw new Error(response.data?.message || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving to final package:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to save to final package');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Button
      onClick={handleSave}
      disabled={disabled || saving}
      variant={saved ? "outline" : "secondary"}
      className={`gap-2 ${className}`}
    >
      {saving ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Saving...
        </>
      ) : saved ? (
        <>
          <CheckCircle className="w-4 h-4 text-green-500" />
          Saved!
        </>
      ) : (
        <>
          <Save className="w-4 h-4" />
          Save to Package
        </>
      )}
    </Button>
  );
}
